import { useState } from "react";
import useApi from "./useApi";
import { API_ROUTES } from "lib/constants/api-constants";
import { DMSServices } from "lib/services/document-services";
import { DMS_PATHS } from "lib/constants/dms-path-constants";
import useDocLinks from "./useDocLinks";
import {
  DEFAULT_NOT_PROVIDED_TREE_NODE,
  DEFAULT_PRACTITIONER_DOCUMENTS,
} from "lib/constants/default-values";
import { formatRoomNumber } from "lib/utils/util";
import { formatDateWithTime } from "lib/utils/date-utils";
import { ASSETS } from "assets";

interface PractitionerState {
  loading: boolean;
  error: string | null;
  practitioners: PractitionerDetails[];

  getPractitionerDetails: (
    practitionerId: string,
    onSuccess?: (details: PractitionerDetails) => void
  ) => void;
  getPractitionerAnalyticsDetails: (
    practitionerId: string,
    onSuccess?: (details: PractitionerAnalyticsDetails) => void
  ) => void;
  getAllPractitionersAnalytics: (
    onSuccess?: (practitionerAnalytics: PractitionerAnalytics[]) => void
  ) => void;
  updatePractitionerDetails: (
    practitionerId: string,
    practitionerDetails: PractitionerDetails,
    onSuccess?: (details: PractitionerDetails) => void
  ) => void;
  getAllPractitioners: (
    onSuccess?: (details: PractitionerDetails[]) => void
  ) => void;
  getActivePractitioners: (
    onSuccess?: (details: PractitionerDetails[]) => void
  ) => void;
  approvePractitioner: (
    practitionerId: string,
    onSuccess?: (details: PractitionerDetails) => void
  ) => void;

  rejectPractitioner: (
    practitionerId: string,
    onSuccess?: (details: PractitionerDetails) => void
  ) => void;
  disablePractitioner: (
    practitionerId: string,
    isDisabled: boolean,
    onSuccess?: (details: PractitionerDetails) => void
  ) => void;
  deletePractitioner: (
    practitionerId: string,
    onSuccess?: (details: PractitionerDetails) => void
  ) => void;

  invitePractitioners: (
    email: string,
    name: string,
    onSuccess?: (email: string) => void
  ) => void;

  signupPractitioner: (
    practitionerBio: PractitionerDetails,
    onSuccess?: (details: PractitionerDetails) => void
  ) => void;

  addPractitionerTherapies: (
    practitionerId: string,
    therapies: PractitionerTherapy[],
    onSuccess?: (practitionerTherapies: PractitionerTherapy[]) => void
  ) => void;

  getPractitionerTherapies: (
    practitionerId: string,
    onSuccess?: (practitionerTherapies: PractitionerTherapy[]) => void
  ) => void;

  deletePractitionerTherapy: (
    practitionerId: string,
    therapyId: string,
    onSuccess?: () => void
  ) => void;

  updatePractitionerProfilePic: (
    practitionerId: string,
    profilePic: File,
    onSuccess?: (profilePicUrl: string) => void
  ) => void;

  getPractitionerProfilePic: (
    practitionerId: string,
    onSuccess?: (profilePicUrl: string) => void
  ) => void;

  getPractitionerDocuments: (
    practitioner: PractitionerDetails,
    subscriptions?: PractitionerSubscriptionDetails[],
    onSuccess?: (documents: PractitionerDocuments) => void
  ) => void;

  getPendingDocuments: (
    practitionerId: string,
    onSuccess?: (practitionerDocuments: PractitionerDocuments) => void
  ) => Promise<PractitionerDocuments>;

  updatePractitionerDocuments: (
    practitionerId: string,
    certificate: File,
    onSuccess?: (url: string) => void
  ) => void;

  uploadPractitionerLicenses: (
    practitionerId: string,
    subscriptionId: string,
    license: File,
    onSuccess?: (url: string) => void
  ) => void;

  isPractitionerSubscribed: (
    practitionerId: string,
    onSuccess?: (isSubscribed: boolean) => void
  ) => void;

  getPractitionerSubscriptions: (
    practitionerId: string,
    onSuccess?: (subscriptions: PractitionerSubscriptionDetails[]) => void
  ) => void;

  getPractitionerSubscriptionsWithPastDates: (
    practitionerId: string,
    onSuccess?: (subscriptions: PractitionerSubscriptionDetails[]) => void
  ) => void;

  postPractitionerSubscriptions: (
    clinicId: string,
    practitionerId: string,
    onSuccess?: (subscriptions: PractitionerSubscriptionDetails[]) => void
  ) => void;

  markSubscriptionAsPaid: (
    practitionerId: string,
    transactionId: string,
    onSuccess?: (subscription: PractitionerSubscription) => void
  ) => void;

  deleteSubscription: (
    transactionId: string,
    onSuccess?: (subscription: PractitionerSubscription) => void
  ) => void;

  cancelSubscription: (
    transactionId: string,
    onSuccess?: (subscription: PractitionerSubscription) => void
  ) => void;

  getCheckoutSession: (
    transactionId: string,
    onSuccess?: (session: CheckoutSessionResponse) => void
  ) => void;

  getPractitionerRoomBookings: (
    practitionerId: string,
    clinicId: string,
    onSuccess?: (roomBookingDetails: RoomBookingsDetails[]) => void
  ) => void;
  getBookingsDetails: (
    practitionerId: string,
    onSuccess?: (bookings: PatientAppointmentBookingDetails[]) => void
  ) => void;

  getAllBookingsDetails: (
    practitionerId: string,
    onSuccess?: (bookings: Appointment[]) => void
  ) => void;

  updatePractitionerQualificationDoc: (
    practitionerId: string,
    qualificationDoc: File,
    onSuccess?: (url: string) => void
  ) => void;

  updatePractitionerInsuranceDoc: (
    practitionerId: string,
    insuranceDoc: File,
    onSuccess?: (url: string) => void
  ) => void;
}

const usePractitioner = (): PractitionerState => {
  const {
    createDocLink,
    error: docLinksError,
    isLoading: docLinksLoading,
    getDocLink,
  } = useDocLinks();
  const {
    getData: getPractitionerDetailsRequest,
    loading: getPractitionerDetailsRequestLoading,
  } = useApi<PractitionerDetails>();

  const {
    getData: getPractitionerAnalyticsDetailsRequest,
    loading: getPractitionerAnalyticsDetailsRequestLoading,
  } = useApi<PractitionerAnalyticsDetails>();

  const {
    getData: getAllPractitionersAnalyticsRequest,
    loading: getAllPractitionersAnalyticsRequestLoading,
  } = useApi<PractitionerAnalytics[]>();

  const {
    putData: updatePractitionerDetailsRequest,
    loading: updatePractitionerDetailsRequestLoading,
  } = useApi<PractitionerDetails>();

  const {
    deleteData: deletePractitionerRequest,
    loading: deletePractitionerRequestLoading,
  } = useApi<PractitionerDetails>();

  const {
    getData: getAllPractitionersRequest,
    loading: getAllPractitionersRequestLoading,
  } = useApi<PractitionerDetails[]>();

  const {
    postData: invitePractitionersRequest,
    loading: invitePractitionersRequestLoading,
  } = useApi();

  const {
    postData: practitionerSignUpRequest,
    loading: practitionerSignUpRequestLoading,
  } = useApi();

  const {
    postData: addUpdatePractitionerTherapiesRequest,
    loading: addUpdatePractitionerTherapiesRequestLoading,
  } = useApi<PractitionerTherapy[]>();

  const {
    getData: getPractitionerTherapiesRequest,
    loading: getPractitionerTherapiesRequestLoading,
  } = useApi<PractitionerTherapy[]>();

  const {
    deleteData: deletePractitionerTherapyRequest,
    loading: deletePractitionerTherapyRequestLoading,
  } = useApi<PractitionerTherapy[]>();

  const {
    getData: getPractitionerSubscriptionsRequest,
    loading: getPractitionerSubscriptionsRequestLoading,
  } = useApi<PractitionerSubscriptionDetails[]>();

  const {
    getData: getPractitionerSubscriptionsWithPastDatesRequest,
    loading: getPractitionerSubscriptionsWithPastDatesRequestLoading,
  } = useApi<PractitionerSubscriptionDetails[]>();

  const {
    postData: postPractitionerSubscriptionsRequest,
    loading: postPractitionerSubscriptionsRequestLoading,
  } = useApi<PractitionerSubscriptionDetails[]>();

  const {
    postData: practitionerSubscriptionSuccessRequest,
    loading: practitionerSubscriptionSuccessRequestLoading,
  } = useApi<PractitionerSubscription>();

  const {
    getData: getCheckoutSessionRequest,
    loading: getCheckoutSessionRequestLoading,
  } = useApi<CheckoutSessionResponse>();

  const {
    getData: getPractitionerRoomBookingsRequest,
    loading: getPractitionerRoomBookingsRequestLoading,
  } = useApi<RoomBookingsDetails[]>();

  const { putData: putDataRequest, loading: putDataRequestLoading } =
    useApi<PractitionerSubscription>();

  const { deleteData: deleteDataRequest, loading: deleteDataRequestLoading } =
    useApi<PractitionerSubscription>();

  const {
    getData: getBookingsDetailsRequest,
    loading: getBookingsDetailsRequestLoading,
  } = useApi<PatientAppointmentBookingDetails[]>();

  const [error, setError] = useState<string | null>();
  const [practitioners, setPractitioners] = useState<PractitionerDetails[]>([]);

  /** Common functions */
  const formatPractitionerSubscriptions = (
    subscriptions: PractitionerSubscriptionDetails[]
  ): PractitionerSubscriptionDetails[] => {
    const practitionerSubscriptions = subscriptions.flatMap(
      (subscription) => subscription
    );

    return practitionerSubscriptions.map((subscription) => {
      return {
        ...subscription,
        internalDates: subscription?.dates?.map((date) => new Date(date)),
      } as PractitionerSubscriptionDetails;
    });
  };
  /** Common functions */

  const getAllPractitioners = async (
    onSuccess?: (response: PractitionerDetails[]) => void
  ) => {
    setError(null);
    await getAllPractitionersRequest(
      API_ROUTES.PRACTITIONERS.base,
      (response) => {
        setPractitioners(response);
        onSuccess && onSuccess(response);
      },
      setError
    );
  };

  const getActivePractitioners = async (
    onSuccess?: (response: PractitionerDetails[]) => void
  ) => {
    setError(null);
    await getAllPractitionersRequest(
      API_ROUTES.PRACTITIONERS.activePractitioners,
      (response) => {
        setPractitioners(response);
        onSuccess && onSuccess(response);
      },
      setError
    );
  };

  const getPractitionerDetails = async (
    practitionerId: string,
    onSuccess?: (details: PractitionerDetails) => void
  ) => {
    setError(null);
    await getPractitionerDetailsRequest(
      `${API_ROUTES.PRACTITIONERS.base}/${practitionerId}`,
      onSuccess,
      setError
    );
  };

  const updatePractitionerDetails = async (
    practitionerId: string,
    practitionerDetails: PractitionerDetails,
    onSuccess?: (details: PractitionerDetails) => void
  ) => {
    setError(null);
    await updatePractitionerDetailsRequest(
      `${API_ROUTES.PRACTITIONERS.base}/${practitionerId}`,
      practitionerDetails,
      onSuccess,
      setError
    );
  };

  const approvePractitioner = async (
    practitionerId: string,
    onSuccess?: (details: PractitionerDetails) => void
  ) => {
    setError(null);
    const endPoint = API_ROUTES.PRACTITIONERS.approve.replace(
      "{{PRACTITIONER_ID}}",
      practitionerId
    );
    await updatePractitionerDetailsRequest(endPoint, {}, onSuccess, setError);
  };

  const rejectPractitioner = async (
    practitionerId: string,
    onSuccess?: (details: PractitionerDetails) => void
  ) => {
    setError(null);
    const endPoint = API_ROUTES.PRACTITIONERS.reject.replace(
      "{{PRACTITIONER_ID}}",
      practitionerId
    );
    await updatePractitionerDetailsRequest(endPoint, {}, onSuccess, setError);
  };

  const invitePractitioners = async (
    email: string,
    name: string,
    onSuccess?: (email: string) => void
  ) => {
    setError(null);
    const request = {
      email,
      name,
    };
    const endPoint = API_ROUTES.PRACTITIONERS.invite;
    await invitePractitionersRequest(endPoint, request, onSuccess, setError);
  };

  const getPractitionerAnalyticsDetails = async (
    id: string,
    onSuccess?: (
      practitionerAnalyticsDetails: PractitionerAnalyticsDetails
    ) => void
  ) => {
    setError(null);
    await getPractitionerAnalyticsDetailsRequest(
      `${API_ROUTES.ANALYTICS.base}${API_ROUTES.ANALYTICS.practitioners}/${id}`,
      onSuccess,
      setError
    );
  };

  const getAllPractitionersAnalytics = async (
    onSuccess?: (practitionerAnalytics: PractitionerAnalytics[]) => void
  ) => {
    setError(null);
    await getAllPractitionersAnalyticsRequest(
      `${API_ROUTES.ANALYTICS.base}${API_ROUTES.ANALYTICS.practitioners}/all`,
      onSuccess,
      setError
    );
  };

  const signupPractitioner = async (
    practitionerBio: PractitionerDetails,
    onSuccess?: (details: PractitionerDetails) => void
  ) => {
    setError(null);
    await practitionerSignUpRequest(
      API_ROUTES.PRACTITIONERS.base,
      practitionerBio,
      onSuccess,
      setError
    );
  };

  const addPractitionerTherapies = async (
    practitionerId: string,
    therapies: PractitionerTherapy[],
    onSuccess?: (practitionerTherapies: PractitionerTherapy[]) => void
  ) => {
    setError(null);
    const endPoint = API_ROUTES.PRACTITIONERS.therapies.replace(
      "{{PRACTITIONER_ID}}",
      practitionerId
    );
    await addUpdatePractitionerTherapiesRequest(
      endPoint,
      therapies.map((item) => ({
        ...item,
        practitionerId: practitionerId,
      })),
      onSuccess,
      setError
    );
  };

  const getPractitionerTherapies = async (
    practitionerId: string,
    onSuccess?: (practitionerTherapies: PractitionerTherapy[]) => void
  ) => {
    setError(null);
    const endPoint = API_ROUTES.PRACTITIONERS.therapies.replace(
      "{{PRACTITIONER_ID}}",
      practitionerId
    );
    await getPractitionerTherapiesRequest(endPoint, onSuccess, setError);
  };

  const deletePractitionerTherapy = async (
    practitionerId: string,
    therapyId: string,
    onSuccess?: () => void
  ) => {
    setError(null);
    const endPoint = API_ROUTES.PRACTITIONERS.therapy
      .replace("{{PRACTITIONER_ID}}", practitionerId)
      .replace("{{THERAPY_ID}}", therapyId);
    await deletePractitionerTherapyRequest(endPoint, null, onSuccess, setError);
  };

  const getPractitionerProfilePic = async (
    practitionerId: string,
    onSuccess?: (profilePicUrl: string) => void
  ) => {
    // Query docLink to get the profile pic URL
    const docLinkDetails: Partial<DocLinkDetails> = {
      parentId: practitionerId,
      path: `${DMS_PATHS.IMAGES.BASE}${DMS_PATHS.IMAGES.PRACTITIONERS}/${practitionerId}${DMS_PATHS.IMAGES.PRACTITIONER_PROFILE}`,
    };

    getDocLink(docLinkDetails.parentId, docLinkDetails.path, (response) =>
      onSuccess(response?.[0]?.url ?? ASSETS.IMAGES.PLACEHOLDER_PROFILE_PIC)
    );
  };

  const updatePractitionerProfilePic = async (
    practitionerId: string,
    profilePic: File,
    onSuccess?: (profilePicUrl: string) => void
  ) => {
    const docLinkDetails: DocLinkDetails = {
      parentId: practitionerId,
      parentType: "practitioner",
      path: `${DMS_PATHS.IMAGES.BASE}${DMS_PATHS.IMAGES.PRACTITIONERS}/${practitionerId}${DMS_PATHS.IMAGES.PRACTITIONER_PROFILE}`,
      title: profilePic.name,
      url: "", // <-- This will be updated after upload
    };

    // Compress file
    const compressedFile = await DMSServices.compressImage(profilePic);

    // Upload file to DMS
    DMSServices.uploadDocument(
      compressedFile ?? profilePic,
      docLinkDetails.parentId,
      docLinkDetails.parentType,
      docLinkDetails.path,
      "",
      (response) => {
        // Create DocLink
        docLinkDetails.url = response.url;
        createDocLink(docLinkDetails, () => onSuccess(docLinkDetails?.url));
      }
    );
  };

  const getDocUrl = async (
    parentId: string,
    path: string
  ): Promise<DocLinkDetails[]> => {
    return new Promise((resolve, reject) => {
      getDocLink(parentId, path, (response) => {
        if (response?.length > 0) {
          resolve(response ?? []);
        } else {
          reject(new Error("Document not found"));
        }
      });
    });
  };

  const getSubscriptionLicenses = async (
    subscriptions: PractitionerSubscriptionDetails[]
  ): Promise<TreeNode> => {
    if (subscriptions?.length > 0) {
      const activeSubscriptions = subscriptions.filter(
        (subscription) => subscription?.isActive
      );

      const allLicenses = await Promise.all(
        activeSubscriptions.map(async (subscription) => {
          const path = `${DMS_PATHS.DOCUMENTS.BASE}${DMS_PATHS.DOCUMENTS.PRACTITIONERS}/${subscription?.practitionerId}${DMS_PATHS.DOCUMENTS.PRACTITIONER_LATEST_CONTRACT}/${subscription?.id}`;

          const licenses = await getDocUrl(subscription?.practitionerId, path);
          return Promise.resolve({
            subscription,
            docs: licenses,
          });
        })
      );

      const licenseNode: TreeNode = {
        id: "licenses",
        name: "Licenses",
        children: [],
      };

      allLicenses.forEach((license) => {
        if (license?.docs?.length > 0) {
          licenseNode.children.push({
            id: license.subscription.id,
            name: `Subscription: ${
              license.subscription.clinicName
            }-${formatRoomNumber(license.subscription.room)}`,
            children: license.docs.map((doc, index) => ({
              id: `${license.subscription.id}-${index}`,
              name: doc.title,
              url: doc.url,
            })),
          });
        }
      });

      console.log("All Licenses:", allLicenses);
      return Promise.resolve(licenseNode);
    }

    return Promise.resolve({
      id: "licenses",
      name: "Licenses",
      children: [
        {
          id: "no_licenses",
          name: "No Licenses Found",
          children: [],
        },
      ],
    });
  };

  const getPendingDocuments = async (
    practitionerId: string,
    onSuccess?: (practitionerDocuments: PractitionerDocuments) => void
  ) => {
    const practitionerDocuments: PractitionerDocuments =
      DEFAULT_PRACTITIONER_DOCUMENTS;

    const practitionerNode: TreeNode = {
      id: practitionerId,
      name: "Practitioner Documents",
      children: [],
    };

    // Query docLink to get the profile pic URL
    const profilePicPath =
      DMS_PATHS.PRACTITIONERS.PRACTITIONER_PROFILE_PIC_PATH.replace(
        "{{PRACTITIONER_ID}}",
        practitionerId
      );

    const profilePic = await getDocLink(practitionerId, profilePicPath);

    const profilePicRootNode: TreeNode = {
      id: "profile_pic_root",
      name: "Profile Picture",
      children: [],
    };

    if (profilePic?.length > 0) {
      const profilePicNode: TreeNode = {
        id: profilePic[0].id,
        name: profilePic[0].title,
        url: profilePic[0].url,
      };

      profilePicRootNode.children.push(profilePicNode);
    } else {
      profilePicRootNode.children.push({
        ...DEFAULT_NOT_PROVIDED_TREE_NODE,
        id: "profile_pic",
      });
    }

    practitionerNode.children.push(profilePicRootNode);

    practitionerDocuments.profileImageUrl =
      profilePic?.length === 0 ? null : profilePic?.[0]?.url;

    // Query docLink to get the subscription licenses URL
    const subscriptionLicensesPath =
      DMS_PATHS.PRACTITIONERS.PRACTITIONER_SUBSCRIPTION_LICENSES_PATH.replace(
        "{{PRACTITIONER_ID}}",
        practitionerId
      ).replace("{{SUBSCRIPTION_ID}}", "%");

    const subscriptionLicenses = await getDocLink(
      practitionerId,
      subscriptionLicensesPath
    );

    const subscriptionLicensesRootNode: TreeNode = {
      id: "subscription_licenses_root",
      name: "Subscription Licenses",
      children: [],
    };

    if (subscriptionLicenses?.length > 0) {
      const subscriptionLicensesNodes: TreeNode[] = subscriptionLicenses.map(
        (license) =>
          ({
            id: license.id,
            name: license.title,
            url: license.url,
          } as TreeNode)
      );

      subscriptionLicensesRootNode.children.push(...subscriptionLicensesNodes);
    } else {
      subscriptionLicensesRootNode.children.push({
        ...DEFAULT_NOT_PROVIDED_TREE_NODE,
        id: "subscription_licenses",
      });
    }

    practitionerDocuments.subscriptionContracts =
      subscriptionLicenses?.length === 0
        ? null
        : subscriptionLicenses?.map((license) => license.url);

    practitionerNode.children.push(subscriptionLicensesRootNode);

    // Query docLink to get the educational certificate URL
    const educationalCertificatePath =
      DMS_PATHS.PRACTITIONERS.PRACTITIONER_EDUCATION_CERTIFICATE_PATH.replace(
        "{{PRACTITIONER_ID}}",
        practitionerId
      );

    const educationalCertificate = await getDocLink(
      practitionerId,
      educationalCertificatePath
    );

    const educationalCertificateRootNode: TreeNode = {
      id: "educational_certificate_root",
      name: "Educational Certificate",
      children: [],
    };

    if (educationalCertificate?.length > 0) {
      const educationalCertificateNode: TreeNode = {
        id: educationalCertificate[0].id,
        name: educationalCertificate[0].title,
        url: educationalCertificate[0].url,
      };

      educationalCertificateRootNode.children.push(educationalCertificateNode);
    } else {
      educationalCertificateRootNode.children.push({
        ...DEFAULT_NOT_PROVIDED_TREE_NODE,
        id: "educational_certificate",
      });
    }

    practitionerDocuments.educationalCertificateUrl =
      educationalCertificate?.length === 0
        ? null
        : educationalCertificate?.[0]?.url;

    practitionerNode.children.push(educationalCertificateRootNode);

    practitionerDocuments.documents = [practitionerNode];

    onSuccess?.(practitionerDocuments);

    return Promise.resolve(practitionerDocuments);
  };

  const getPractitionerDocuments = async (
    practitioner: PractitionerDetails,
    subscriptions?: PractitionerSubscriptionDetails[],
    onSuccess?: (documents: PractitionerDocuments) => void
  ) => {
    const documents = await getPendingDocuments(practitioner?.id, () => {});

    if (!documents) {
      onSuccess?.(null);
      return;
    }

    // const practitionerNode: TreeNode = {
    //   id: practitioner.id,
    //   name: `${practitioner.firstName} ${practitioner.lastName}`,
    //   children: [], // <- Add profile pic node
    // };

    documents.documents[0].name = `${practitioner?.firstName} ${practitioner?.lastName}`;

    onSuccess?.(documents);
  };

  const updatePractitionerDocuments = async (
    practitionerId: string,
    certificate: File,
    onSuccess?: (url: string) => void
  ) => {
    const docLinkDetails: DocLinkDetails = {
      parentId: practitionerId,
      parentType: "practitioner",
      path: `${DMS_PATHS.DOCUMENTS.BASE}${DMS_PATHS.DOCUMENTS.PRACTITIONERS}/${practitionerId}${DMS_PATHS.DOCUMENTS.PRACTITIONER_CERTIFICATE}`,
      title: certificate?.name,
      url: "", // <-- This will be updated after upload
    };
    // Upload file to DMS

    DMSServices.uploadDocument(
      certificate,
      docLinkDetails.parentId,
      docLinkDetails.parentType,
      docLinkDetails.path,
      "",
      (response) => {
        // Create DocLink
        docLinkDetails.url = response.url;
        createDocLink(docLinkDetails, () => onSuccess(docLinkDetails.url));
      }
    );
  };

  const uploadPractitionerLicenses = async (
    practitionerId: string,
    subscriptionId: string,
    license: File,
    onSuccess?: (url: string) => void
  ) => {
    const docLinkDetails: DocLinkDetails = {
      parentId: practitionerId,
      parentType: "practitioner",
      path: `${DMS_PATHS.DOCUMENTS.BASE}${DMS_PATHS.DOCUMENTS.PRACTITIONERS}/${practitionerId}${DMS_PATHS.DOCUMENTS.PRACTITIONER_LATEST_CONTRACT}/${subscriptionId}`,
      title: license?.name,
      url: "", // <-- This will be updated after upload
    };
    // Upload file to DMS

    return DMSServices.uploadDocument(
      license,
      docLinkDetails.parentId,
      docLinkDetails.parentType,
      docLinkDetails.path,
      "",
      (response) => {
        // Create DocLink
        docLinkDetails.url = response.url;
        createDocLink(docLinkDetails, () => {
          onSuccess?.(docLinkDetails.url);
          return Promise.resolve(docLinkDetails.url);
        });
      }
    );
  };

  const updatePractitionerQualificationDoc = async (
    practitionerId: string,
    qualificationDoc: File,
    onSuccess?: (url: string) => void
  ) => {
    const docLinkDetails: DocLinkDetails = {
      parentId: practitionerId,
      parentType: "practitioner",
      path: DMS_PATHS.PRACTITIONERS.PRACTITIONER_QUALIFICATION_DOC_PATH.replace(
        "{{PRACTITIONER_ID}}",
        practitionerId
      ),
      title: qualificationDoc?.name,
      url: "",
    };
    DMSServices.uploadDocument(
      qualificationDoc,
      docLinkDetails.parentId,
      docLinkDetails.parentType,
      docLinkDetails.path,
      "",
      (response) => {
        docLinkDetails.url = response.url;
        createDocLink(docLinkDetails, () => onSuccess?.(docLinkDetails.url));
      }
    );
  };

  const updatePractitionerInsuranceDoc = async (
    practitionerId: string,
    insuranceDoc: File,
    onSuccess?: (url: string) => void
  ) => {
    const docLinkDetails: DocLinkDetails = {
      parentId: practitionerId,
      parentType: "practitioner",
      path: DMS_PATHS.PRACTITIONERS.PRACTITIONER_INSURANCE_DOC_PATH.replace(
        "{{PRACTITIONER_ID}}",
        practitionerId
      ),
      title: insuranceDoc?.name,
      url: "",
    };
    DMSServices.uploadDocument(
      insuranceDoc,
      docLinkDetails.parentId,
      docLinkDetails.parentType,
      docLinkDetails.path,
      "",
      (response) => {
        docLinkDetails.url = response.url;
        createDocLink(docLinkDetails, () => onSuccess?.(docLinkDetails.url));
      }
    );
  };

  const isPractitionerSubscribed = async (
    practitionerId: string,
    onSuccess?: (isSubscribed: boolean) => void
  ) => {
    await getPractitionerDetails(practitionerId, (practitioner) => {
      if (practitioner) {
        onSuccess?.(practitioner.isActive);
      }

      onSuccess?.(false);
    });
  };

  const getPractitionerSubscriptions = (
    practitionerId: string,
    onSuccess?: (subscriptions: PractitionerSubscriptionDetails[]) => void
  ) => {
    setError(null);
    const endPoint = API_ROUTES.PRACTITIONERS.subscriptions.replace(
      "{{PRACTITIONER_ID}}",
      practitionerId
    );
    getPractitionerSubscriptionsRequest(
      endPoint,
      (subscriptions) => {
        onSuccess?.(formatPractitionerSubscriptions(subscriptions));
      },
      setError
    );
  };

  const getPractitionerSubscriptionsWithPastDates = (
    practitionerId: string,
    onSuccess?: (subscriptions: PractitionerSubscriptionDetails[]) => void
  ) => {
    setError(null);
    const endPoint = API_ROUTES.PRACTITIONERS.subscriptionswithpastdates.replace(
      "{{PRACTITIONER_ID}}",
      practitionerId
    );
    getPractitionerSubscriptionsWithPastDatesRequest(
      endPoint,
      (subscriptions) => {
        onSuccess?.(formatPractitionerSubscriptions(subscriptions));
      },
      setError
    );
  };

  const postPractitionerSubscriptions = (
    clinicId: string,
    practitionerId: string,
    onSuccess?: (subscriptions: PractitionerSubscriptionDetails[]) => void
  ) => {
    setError(null);
    const endPoint = API_ROUTES.PRACTITIONERS.subscriptions.replace(
      "{{PRACTITIONER_ID}}",
      practitionerId
    );
    postPractitionerSubscriptionsRequest(
      endPoint,
      {clinicId, practitionerId},
      (subscriptions) => {
        onSuccess?.(formatPractitionerSubscriptions(subscriptions));
      },
      setError
    );
  };

  const markSubscriptionAsPaid = async (
    practitionerId: string,
    transactionId: string,
    onSuccess?: (subscription: PractitionerSubscription) => void
  ) => {
    setError(null);
    const endPoint = API_ROUTES.PRACTITIONERS.practitionerSubscriptionSuccess
      .replace("{{PRACTITIONER_ID}}", practitionerId)
      .replace("{{TRANSACTION_ID}}", transactionId);
    await practitionerSubscriptionSuccessRequest(
      endPoint,
      {},
      onSuccess,
      setError
    );
  };

  const deleteSubscription = async (
    transactionId: string,
    onSuccess?: (subscription: PractitionerSubscription) => void
  ) => {
    // /checkout/:transactionId/cancel""
    setError(null);
    const endPoint =
      API_ROUTES.PRACTITIONERS.cancelPractitionerSubscription(transactionId);

    await deleteDataRequest(endPoint, {}, onSuccess, setError);
  };

  const cancelSubscription = async (
    transactionId: string,
    onSuccess?: (subscription: PractitionerSubscription) => void
  ) => {
    // /checkout/:transactionId/cancel""
    setError(null);
    const endPoint =
      API_ROUTES.PRACTITIONERS.cancelPractitionerSubscription(transactionId);

    await putDataRequest(endPoint, {}, onSuccess, setError);
  };

  const getCheckoutSession = async (
    transactionId: string,
    onSuccess?: (session: CheckoutSessionResponse) => void
  ) => {
    setError(null);
    const endPoint = API_ROUTES.PRACTITIONERS.getCheckoutSession.replace(
      "{{TRANSACTION_ID}}",
      transactionId
    );
    await getCheckoutSessionRequest(endPoint, onSuccess, setError);
  };

  const getPractitionerRoomBookings = async (
    practitionerId: string,
    clinicId: string,
    onSuccess?: (roomBookingDetails: RoomBookingsDetails[]) => void
  ) => {
    setError(null);
    const endPoint =
      API_ROUTES.PRACTITIONER_ROOM_BOOKINGS.getPractitionerRoomBookings
        .replace("{{PRACTITIONER_ID}}", practitionerId)
        .replace("{{CLINIC_ID}}", clinicId);
    await getPractitionerRoomBookingsRequest(endPoint, onSuccess, setError);
  };

  const getBookingsDetails = async (
    practitionerId: string,
    onSuccess?: (bookings: PatientAppointmentBookingDetails[]) => void
  ) => {
    const endPoint =
      API_ROUTES.APPOINTMENTS.byPractitionerWithDetails(practitionerId);
    await getBookingsDetailsRequest(endPoint, onSuccess, setError);
  };
  const getAllBookingsDetails = async (
    practitionerId: string,
    onSuccess?: (bookings: Appointment[]) => void
  ) => {
    const endPoint =
      API_ROUTES.APPOINTMENTS.allBookingsbyPractitioner(practitionerId);
    await getBookingsDetailsRequest(
      endPoint,
      (values) => {
        const response = values.map((booking) => ({
          date: booking.fromTime,
          time: formatDateWithTime(booking.fromTime),
          status: booking.status,
          patientName: booking.patientName,
          practitionerName: booking.practitionerName,
          clinicName: booking.clinicName,
        }));
        onSuccess?.(response);
      },
      setError
    );
  };

  const disablePractitioner = async (
    practitionerId: string,
    isDisabled: boolean,
    onSuccess?: (details: PractitionerDetails) => void
  ) => {
    setError(null);
    const endPoint =
      API_ROUTES.PRACTITIONERS.disablePractitioner(practitionerId);
    await updatePractitionerDetailsRequest(
      endPoint,
      { isDisabled },
      onSuccess,
      setError
    );
  };

  const deletePractitioner = async (
    practitionerId: string,
    onSuccess?: (details: PractitionerDetails) => void
  ) => {
    setError(null);
    const endPoint =
      API_ROUTES.PRACTITIONERS.deletePractitioner(practitionerId);
      console.log("endPoint: ", endPoint);
    await deletePractitionerRequest(
      endPoint,
      {},
      onSuccess,
      setError
    );
  };

  return {
    loading:
      getPractitionerDetailsRequestLoading ||
      updatePractitionerDetailsRequestLoading ||
      getAllPractitionersRequestLoading ||
      invitePractitionersRequestLoading ||
      getPractitionerAnalyticsDetailsRequestLoading ||
      getAllPractitionersAnalyticsRequestLoading ||
      practitionerSignUpRequestLoading ||
      addUpdatePractitionerTherapiesRequestLoading ||
      getPractitionerTherapiesRequestLoading ||
      deletePractitionerTherapyRequestLoading ||
      docLinksLoading ||
      getPractitionerSubscriptionsRequestLoading ||
      getPractitionerSubscriptionsWithPastDatesRequestLoading ||
      postPractitionerSubscriptionsRequestLoading ||
      practitionerSubscriptionSuccessRequestLoading ||
      getCheckoutSessionRequestLoading ||
      getPractitionerRoomBookingsRequestLoading ||
      getBookingsDetailsRequestLoading || 
      deletePractitionerRequestLoading,
    error: error || docLinksError,
    practitioners,
    getPractitionerDetails,
    getPractitionerAnalyticsDetails,
    getAllPractitionersAnalytics,
    updatePractitionerDetails,
    getAllPractitioners,
    invitePractitioners,
    approvePractitioner,
    rejectPractitioner,
    signupPractitioner,
    addPractitionerTherapies,
    getPractitionerTherapies,
    deletePractitionerTherapy,
    updatePractitionerProfilePic,
    getPractitionerProfilePic,
    updatePractitionerDocuments,
    updatePractitionerQualificationDoc,
    updatePractitionerInsuranceDoc,
    getPractitionerDocuments,
    isPractitionerSubscribed,
    getPractitionerSubscriptions,
    getPractitionerSubscriptionsWithPastDates,
    postPractitionerSubscriptions,
    markSubscriptionAsPaid,
    deleteSubscription,
    cancelSubscription,
    getActivePractitioners,
    getCheckoutSession,
    uploadPractitionerLicenses,
    getPendingDocuments,
    getPractitionerRoomBookings,
    getBookingsDetails,
    getAllBookingsDetails,
    disablePractitioner,
    deletePractitioner,
  };
};
export default usePractitioner;
